'use client'

import ms from 'ms'
import { AuthButton } from '@repo/studio-components/auth/auth-button'
import { IconHolder } from '@repo/studio-components-icon-holder'
import { ChevronLeftIcon } from '@repo/studio-design-system-ui-icons/arrows/chevron-left'
import { Z_INDEX_MODALS } from 'constants/modals'
import { animate, AnimatePresence, domMax, LazyMotion, m, useMotionValue } from 'framer-motion'
import * as React from 'react'
import { tw } from 'utils/classnames'
import { readPeekRatio, writePeekRatio } from '../../hooks/use-floating-button-position'
import { openAidaOverlayFromElement } from '../../stores/overlay-store'
import { useAidaImageDrop } from '../../hooks/use-aida-image-drop'
import { AidaIconAurora } from '../icon/aida-icon'
import { AidaBubbleDropFrame } from './aida-image-drop-chrome'
import { AidaGeneratingRing } from './aida-generating-ring'

const PEEK_SIZE_PX = 48
const PEEK_SLIVER_PX = 22
const TUCK_OFFSET_PX = PEEK_SIZE_PX - PEEK_SLIVER_PX
const EDGE_INSET_PX = 12
const RETUCK_MS = ms('3s')
const SCROLL_DELTA_PX = 12
const SLIDE_S = ms('280ms') / ms('1s')
const SLIDE_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1]
const SURFACE = 'border border-gray-100 bg-service-white-white shadow-lg'

type PeekState = 'tucked' | 'expanded' | 'hidden'
type PeekGlyph = 'peek' | 'icon'

function slideTarget(state: PeekState, width: number) {
  switch (state) {
    case 'tucked': {
      return TUCK_OFFSET_PX
    }
    case 'expanded': {
      return 0
    }
    case 'hidden': {
      return width + EDGE_INSET_PX
    }
  }
}

function glyphFor(state: PeekState, held: PeekGlyph): PeekGlyph {
  switch (state) {
    case 'hidden': {
      return held
    }
    case 'expanded': {
      return 'icon'
    }
    case 'tucked': {
      return 'peek'
    }
  }
}

function yForRatio(ratio: number, viewportHeight: number) {
  const maxY = Math.max(EDGE_INSET_PX, viewportHeight - PEEK_SIZE_PX - EDGE_INSET_PX)
  return Math.min(maxY, Math.max(EDGE_INSET_PX, ratio * viewportHeight - PEEK_SIZE_PX / 2))
}

function peekStateAfterScroll(current: PeekState, delta: number): PeekState {
  if (Math.abs(delta) < SCROLL_DELTA_PX) {
    return current
  }
  if (delta > 0) {
    return 'hidden'
  }
  if (current === 'hidden') {
    return 'tucked'
  }
  return current
}

function readScrollTop(target: EventTarget | null) {
  if (target === document) {
    return { target, scrollTop: window.scrollY }
  }
  if (target instanceof HTMLElement) {
    return { target, scrollTop: target.scrollTop }
  }
  return undefined
}

function scrollDelta(event: Event, lastScrollTop: WeakMap<EventTarget, number>) {
  const reading = readScrollTop(event.target)
  if (reading === undefined) {
    return undefined
  }
  const previous = lastScrollTop.get(reading.target)
  lastScrollTop.set(reading.target, reading.scrollTop)
  if (previous === undefined) {
    return undefined
  }
  return reading.scrollTop - previous
}

function usePeekSlide(state: PeekState) {
  const x = useMotionValue(TUCK_OFFSET_PX)
  const rowRef = React.useRef<HTMLButtonElement>(null)

  React.useLayoutEffect(() => {
    const width = rowRef.current?.offsetWidth ?? PEEK_SIZE_PX
    const target = slideTarget(state, width)
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const animation = animate(x, target, {
      duration: reduced ? 0 : SLIDE_S,
      ease: SLIDE_EASE,
    })
    return () => {
      animation.stop()
    }
  }, [state, x])

  return { x, rowRef }
}

function usePeekOffset(viewportHeight: number) {
  const y = useMotionValue(yForRatio(readPeekRatio(), viewportHeight))

  React.useEffect(() => {
    y.set(yForRatio(readPeekRatio(), viewportHeight))
  }, [viewportHeight, y])

  function onDragEnd() {
    const ratio = Math.min(1, Math.max(0, (y.get() + PEEK_SIZE_PX / 2) / viewportHeight))
    writePeekRatio(ratio)
  }

  return { y, onDragEnd }
}

function useRetuckWhenExpanded(state: PeekState, setState: (next: PeekState) => void) {
  React.useEffect(() => {
    if (state !== 'expanded') {
      return
    }
    const timeoutId = window.setTimeout(() => setState('tucked'), RETUCK_MS)
    return () => window.clearTimeout(timeoutId)
  }, [setState, state])
}

function useHideOnScrollDown(setState: React.Dispatch<React.SetStateAction<PeekState>>) {
  React.useEffect(() => {
    const lastScrollTop = new WeakMap<EventTarget, number>()

    function onScroll(event: Event) {
      const delta = scrollDelta(event, lastScrollTop)
      if (delta === undefined) {
        return
      }
      setState(current => peekStateAfterScroll(current, delta))
    }

    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => document.removeEventListener('scroll', onScroll, { capture: true })
  }, [setState])
}

function openFromExpanded(event: React.MouseEvent<HTMLButtonElement>, loggedIn: boolean) {
  if (!loggedIn) {
    return false
  }
  openAidaOverlayFromElement(event.currentTarget)
  return true
}

function PeekGlyphFace({ glyph, generating }: { glyph: PeekGlyph; generating: boolean }) {
  return (
    <span
      className={tw(
        SURFACE,
        'relative inline-flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full',
      )}
    >
      {generating ? <AidaGeneratingRing /> : null}
      <AnimatePresence initial={false}>
        {glyph === 'icon' ? (
          <m.span
            key="icon"
            className="flex"
            initial={{ opacity: 0, scale: 0.45 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.45 }}
            transition={{ duration: SLIDE_S, ease: SLIDE_EASE }}
          >
            <AidaIconAurora className="size-5" />
          </m.span>
        ) : (
          <m.span
            key="peek"
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.55, x: 14 }}
            transition={{ duration: SLIDE_S, ease: SLIDE_EASE }}
            className="absolute inset-0 flex items-center justify-start pl-1 text-gray-500"
          >
            <IconHolder size="small">
              <ChevronLeftIcon />
            </IconHolder>
          </m.span>
        )}
      </AnimatePresence>
    </span>
  )
}

function AskAidaLabel() {
  return (
    <m.span
      initial={{ opacity: 0, x: 16, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 16, scale: 0.96 }}
      transition={{ duration: SLIDE_S, ease: SLIDE_EASE }}
      className={tw(
        SURFACE,
        'whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-gray-700',
      )}
    >
      Ask Aida
    </m.span>
  )
}

function PeekControl({
  loggedIn,
  state,
  glyph,
  generating,
  showLabel,
  onClick,
  rowRef,
}: {
  loggedIn: boolean
  state: PeekState
  glyph: PeekGlyph
  generating: boolean
  showLabel: boolean
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  rowRef: React.Ref<HTMLButtonElement>
}) {
  const button = (
    <button
      ref={rowRef}
      type="button"
      aria-label="Chat with Aida"
      onClick={onClick}
      className="flex cursor-pointer items-center gap-2"
    >
      <AnimatePresence initial={false}>
        {showLabel ? <AskAidaLabel key="ask-aida" /> : null}
      </AnimatePresence>
      <PeekGlyphFace glyph={glyph} generating={generating} />
    </button>
  )

  if (loggedIn || state !== 'expanded') {
    return button
  }

  return <AuthButton>{button}</AuthButton>
}

function AidaPeekTabSurface({ loggedIn, generating }: { loggedIn: boolean; generating: boolean }) {
  const imageDrop = useAidaImageDrop()
  const [state, setState] = React.useState<PeekState>('tucked')
  const [viewportHeight, setViewportHeight] = React.useState(() => window.innerHeight)
  const glyphRef = React.useRef<PeekGlyph>('peek')
  if (state === 'expanded') {
    glyphRef.current = 'icon'
  }
  if (state === 'tucked') {
    glyphRef.current = 'peek'
  }
  const glyph = glyphFor(state, glyphRef.current)
  const showLabel = state === 'expanded' || (state === 'hidden' && glyph === 'icon')
  const { y, onDragEnd } = usePeekOffset(viewportHeight)
  const { x, rowRef } = usePeekSlide(state)
  const justDraggedRef = React.useRef(false)
  useRetuckWhenExpanded(state, setState)
  useHideOnScrollDown(setState)

  React.useEffect(() => {
    function onResize() {
      setViewportHeight(window.innerHeight)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const dragConstraints = {
    top: EDGE_INSET_PX,
    bottom: Math.max(EDGE_INSET_PX, viewportHeight - PEEK_SIZE_PX - EDGE_INSET_PX),
  }

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    if (justDraggedRef.current) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    if (state !== 'expanded') {
      event.preventDefault()
      event.stopPropagation()
      setState('expanded')
      return
    }
    openFromExpanded(event, loggedIn)
  }

  return (
    <LazyMotion features={domMax}>
      <m.div
        ref={imageDrop.originRef}
        data-testid="aida-peek-tab"
        data-aida-peek-tab
        data-peek-state={state}
        drag="y"
        dragMomentum={false}
        dragConstraints={dragConstraints}
        dragElastic={0}
        style={{ x, y, zIndex: Z_INDEX_MODALS.default }}
        className="fixed right-0 top-0 touch-none"
        onDragEnter={imageDrop.onDragEnter}
        onDragOver={imageDrop.onDragOver}
        onDragLeave={imageDrop.onDragLeave}
        onDrop={imageDrop.onDrop}
        onDragStart={() => {
          justDraggedRef.current = true
        }}
        onDragEnd={() => {
          onDragEnd()
          window.setTimeout(() => {
            justDraggedRef.current = false
          }, ms('50ms'))
        }}
      >
        <AidaBubbleDropFrame available={imageDrop.available} over={imageDrop.isOver}>
          <PeekControl
            loggedIn={loggedIn}
            state={state}
            glyph={glyph}
            generating={generating}
            showLabel={showLabel}
            onClick={handleClick}
            rowRef={rowRef}
          />
        </AidaBubbleDropFrame>
      </m.div>
    </LazyMotion>
  )
}

export function AidaPeekTab({ loggedIn, generating }: { loggedIn: boolean; generating: boolean }) {
  return <AidaPeekTabSurface loggedIn={loggedIn} generating={generating} />
}
